/**
 * Types for the shared comments package.
 *
 * Mirrors the schemas in backend/core/schemas.py — keep these in sync
 * if the backend Comment* schemas change.
 */

export type CommentVisibility = "admin" | "public";

/** Identifies the model + row a comment is attached to. Mirrors the
 *  Django GenericForeignKey storage shape: ``content_type`` is the
 *  natural key (``"app.model"``) and ``object_id`` is the row PK. */
export interface CommentTarget {
  /** Django app label, e.g. "crm". */
  app: string;
  /** Lowercased model name, e.g. "tenant". */
  model: string;
  /** Primary key of the row. */
  id: number;
}

/** Convert the structured target into the wire-format ``content_type``
 *  string the backend's ``/api/comments/`` endpoint expects. */
export function targetContentType(target: CommentTarget): string {
  return `${target.app}.${target.model}`;
}

/** Single comment row as returned by the list / create / update endpoints. */
export interface Comment {
  id: number;
  content_type: string;
  object_id: number;
  parent_id: number | null;
  depth: number;
  reply_count: number;
  author_id: number | null;
  author_name: string;
  body: string;
  visibility: CommentVisibility;
  created_at: string;
  edited_at: string | null;
  is_own: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_reply: boolean;
  /** Staff-only: flip a comment between ``admin`` and ``public``.
   *  Tenant-portal users always get ``false`` here — the affordance
   *  isn't rendered for them, and the backend rejects with 403 if
   *  they call the endpoint directly. */
  can_change_visibility: boolean;
}

/** Wrapper response from GET /api/comments/. */
export interface CommentListResponse {
  items: Comment[];
  /** False on tenant-portal — UI hides the visibility selector. */
  can_post_admin: boolean;
  can_post_public: boolean;
}

export interface CommentCreateInput {
  content_type: string;
  object_id: number;
  parent_id?: number | null;
  body: string;
  visibility?: CommentVisibility;
}

export interface CommentUpdateInput {
  /** Optional new body. Stamps ``edited_at`` server-side. */
  body?: string;
  /** Staff-only: flip visibility. The backend audit-logs every
   *  change and 403s if the caller isn't staff. */
  visibility?: CommentVisibility;
}

/** Adapter shape — each consuming app injects its own configured client. */
export interface CommentsApi {
  list(args: {
    content_type: string;
    object_id: number;
    limit?: number;
    offset?: number;
  }): Promise<CommentListResponse>;
  create(body: CommentCreateInput): Promise<Comment>;
  update(id: number, body: CommentUpdateInput): Promise<Comment>;
  remove(id: number, force?: boolean): Promise<void>;
}
